'use client';

import clsx from 'clsx';
import { type ReactNode, useState } from 'react';

import Flex, { type FlexProps } from '@commons/containers/Flex';
import type { Alternates } from '@commons/i18n';

import type { MainMenuLink } from '..';
import MenuPane from './MenuPane';
import SearchPane from './SearchPane';

// Type definition for the mobile menu properties.

type Props = FlexProps & {
  children: ReactNode;
  menu: MainMenuLink[];
  alternates: Alternates;
  displaySearch?: boolean;
  displayMainCTA?: boolean;
};

/**
 * Mobile menu component.
 */

const MobileMenu = (props: Props) => {
  const [activePane, setActivePane] = useState<'menu' | 'search'>();

  const {
    children,
    menu,
    alternates,
    displaySearch = true,
    displayMainCTA = true,
    className,
    ...rest
  } = props;

  const handlePaneTriggerClick = (pane: 'menu' | 'search') => {
    setActivePane(activePane === pane ? undefined : pane);
  };

  const classes = clsx(activePane && 'active', className);

  return (
    <Flex
      width={12}
      theme="white"
      align="center"
      justify="space-between"
      className={classes}
      {...rest}
    >
      {children}
      <Flex gap="xxs" align="center">
        <MenuPane
          menu={menu}
          visible={activePane === 'menu'}
          alternates={alternates}
          displayMainCTA={displayMainCTA}
          handleTriggerClick={() => handlePaneTriggerClick('menu')}
        />
        {displaySearch && (
          <SearchPane
            visible={activePane === 'search'}
            handleTriggerClick={() => handlePaneTriggerClick('search')}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default MobileMenu;
